/**
 * NY State Driver's License / ID Card Parser
 * Parses AAMVA PDF417 barcode data from ID card scanners
 *
 * Format: Starts with @ followed by ANSI header, then key-value pairs
 * Each field is identified by a 3-letter code (e.g., DAQ = ID Number, DCS = Last Name)
 */

import { IHeightExact } from '@/models/master.names';
import { formatHeightDisplay } from '@/utils/common';

export interface ParsedIDCard {
  idNumber: string;
  customerIdNumber: string;
  documentNumber: string;
  state: string;
  issueDate: string;
  expirationDate: string;
  revisionDate: string;
  vehicleClass: string;
  restrictionCodes: string;
  endorsementCodes: string;
  documentDiscriminator: string;
  lastName: string;
  firstName: string;
  middleName: string;
  nameSuffix: string;
  dateOfBirth: string;
  sex: string;
  eyeColor: string;
  height: string;
  addressNumber: string;
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  addressState: string;
  zipCode: string;
  country: string;
}

export interface IDCardParseResult {
  success: boolean;
  data: ParsedIDCard | null;
  error?: string;
  rawData?: string;
}

/**
 * AAMVA Field Code Reference:
 * - DAQ: Customer ID / License Number
 * - DCF: Document Number
 * - DAJ: Jurisdiction Code (State) - used for both license and address state
 * - DBD: Document Issue Date
 * - DBA: Document Expiration Date
 * - DDB: Document Revision Date
 * - DCA: Vehicle Class
 * - DCB: Restriction Codes
 * - DCD: Endorsement Codes
 * - DCK: Document Discriminator / Inventory Control Number
 * - DCS: Last Name (Family Name)
 * - DAC: First Name (standard)
 * - DCT: First Name (alternate, used by some states like NY)
 * - DAD: Middle Name
 * - DCU: Name Suffix
 * - DBB: Date of Birth
 * - DBC: Sex (1=M, 2=F, 9=Unknown)
 * - DAY: Eye Color
 * - DAU: Height
 * - DAG: Street Address Line 1
 * - DAH: Street Address Line 2
 * - DAI: City
 * - DAK: ZIP/Postal Code
 * - DCG: Country
 */

// Sex codes mapped to form values ('M', 'F') matching Gender enum
const SEX_CODES: Record<string, string> = {
  '1': 'M',
  '2': 'F',
  '9': 'U',
};

const EYE_COLOR_CODES: Record<string, string> = {
  BLK: 'BLACK',
  BLU: 'BLUE',
  BRO: 'BROWN',
  GRY: 'GRAY',
  GRN: 'GREEN',
  HAZ: 'HAZEL',
  MAR: 'MAROON',
  PNK: 'PINK',
  DIC: 'DICHROMATIC',
  UNK: 'UNKNOWN',
};

/**
 * Parse date from AAMVA format (MMDDYYYY or YYYYMMDD) to ISO format
 */
function parseDate(dateStr: string): string {
  if (!dateStr || dateStr.length < 8) return '';

  if (dateStr.length === 8) {
    const month = dateStr.substring(0, 2);
    const day = dateStr.substring(2, 4);
    const year = dateStr.substring(4, 8);

    if (parseInt(month) > 12) {
      const y = dateStr.substring(0, 4);
      const m = dateStr.substring(4, 6);
      const d = dateStr.substring(6, 8);
      return `${y}-${m}-${d}`;
    }

    return `${year}-${month}-${day}`;
  }

  return dateStr;
}

/**
 * Parse ZIP code from AAMVA format to standard 5-digit US postal format
 * AAMVA can have 9-11 characters (5 digit zip + 4 digit extension)
 * Returns only the 5-digit base ZIP (backend max length is 5)
 */
function parseZipCode(zipStr: string): string {
  if (!zipStr) return '';

  const cleaned = zipStr.replace(/[^0-9]/g, '');

  if (cleaned.length >= 5) {
    return cleaned.substring(0, 5);
  }

  return cleaned;
}

/**
 * Parse height from AAMVA format (e.g., "509" = 5'09") to feet/inches display.
 */
function parseHeight(heightStr: string): string {
  return formatHeightDisplay(parseHeightToExact(heightStr));
}

export function parseHeightToExact(heightStr: string): IHeightExact | null {
  if (!heightStr) return null;

  const trimmedHeight = heightStr.trim();
  const feetInchesMatch = trimmedHeight.match(
    /^(\d+)\s*'\s*(\d+(?:\.\d+)?)\s*"?$/,
  );

  if (feetInchesMatch) {
    return {
      feet: Number(feetInchesMatch[1]),
      inches: Number(feetInchesMatch[2]),
    };
  }

  if (/^\d{3}$/.test(trimmedHeight)) {
    return {
      feet: Number(trimmedHeight.charAt(0)),
      inches: Number(trimmedHeight.substring(1)),
    };
  }

  const decimalFeet = Number(trimmedHeight);
  if (!Number.isNaN(decimalFeet)) {
    const feet = Math.floor(decimalFeet);
    const inches = Number(((decimalFeet - feet) * 12).toFixed(2));
    const normalizedFeet = inches >= 12 ? feet + 1 : feet;

    return {
      feet: normalizedFeet,
      inches: inches >= 12 ? 0 : inches,
    };
  }

  return null;
}

/**
 * USPS Publication 28 (Appendix C2) secondary-unit designators that are always
 * written with a unit identifier after them. Requiring that identifier is what
 * keeps real street names intact: "SUITE ST", "FLOOR ST" and "STE GENEVIEVE DR"
 * never split because no unit number follows.
 *
 * Deliberately excluded:
 * - Standalone positional designators (REAR, FRNT/FRONT, UPPR/UPPER,
 *   LOWR/LOWER, BSMT/BASEMENT, PH/PENTHOUSE, LBBY/LOBBY, OFC/OFFICE): they
 *   carry no unit number, so matching them means matching a bare word, and each
 *   is a common street token ("100 FRONT ST", "100 UPPER MOUNTAIN RD").
 * - Marina/airfield designators (KEY, PIER, SLIP, HNGR/HANGAR): near-zero
 *   frequency on a license, high collision with street names ("KEY WEST DR").
 * - NO/NO./NUM: collides with the NORTH abbreviation.
 */
const UNIT_DESIGNATORS = [
  'APARTMENT',
  'APT',
  'BUILDING',
  'BLDG',
  'DEPARTMENT',
  'DEPT',
  'FLOOR',
  'FL',
  'LOT',
  'ROOM',
  'RM',
  'SPACE',
  'SPC',
  'SUITE',
  'STE',
  'TRAILER',
  'TRLR',
  'UNIT',
];

const UNIT_DESIGNATOR_ALTERNATION = UNIT_DESIGNATORS.join('|');

// A unit identifier must contain a digit ("212", "4B", "B12", "4-B", "2/3") or
// be a single letter ("APT A"). Multi-letter digit-free words are rejected so
// "STE GENEVIEVE" and "SPACE CENTER" stay part of the street name.
const UNIT_IDENTIFIER =
  '(?=[A-Za-z0-9]*\\d|[A-Za-z](?![A-Za-z0-9]))' +
  '[A-Za-z0-9]+(?:[-/][A-Za-z0-9]+)*';

// Trailing punctuation on the unit ("APT 4B.") and anything after it
// ("# 212 REAR") belong to the sub-address, not the street.
const UNIT_TAIL = '[.,;]?(?:\\s.*)?';

/**
 * Group 1 = street name, group 2 = "#" sub-address, group 3 = word-designator
 * sub-address (either one runs from the designator to the end of the line).
 *
 * The leading `(.*?\S)` requires at least one token before the designator, so a
 * line that is only a designator ("# 212") keeps its value in the street name
 * instead of emptying it. A word designator must additionally be preceded by
 * whitespace, which gives the word boundary that protects "UNION AVE",
 * "UNITY DR" and "FLORAL PARK RD"; a bare "#" needs no separator so it also
 * splits when it sits flush against the street ("COLLINS AVE#212"). The lazy
 * `.*?` takes the leftmost designator, so "MAIN ST BLDG 3 APT 12" yields
 * "BLDG 3 APT 12" as one sub-address rather than leaving "BLDG 3" on the street.
 */
const SUB_ADDRESS_PATTERN = new RegExp(
  [
    '^(.*?\\S)',
    '(?:',
    `\\s*(#\\s*${UNIT_IDENTIFIER}${UNIT_TAIL})`,
    '|',
    `\\s+((?:${UNIT_DESIGNATOR_ALTERNATION})\\.?[\\s#]+${UNIT_IDENTIFIER}${UNIT_TAIL})`,
    ')$',
  ].join(''),
  'i',
);

/**
 * A split that leaves one of these as the street name means the pattern ate the
 * real street: a mailing-address form whose number is not a unit at all
 * ("PO BOX # 123"), a lone directional ("N LOT 6 RD"), or a bare designator
 * ("APT #4B" as the whole line). Such a match is discarded and the line is left
 * intact.
 */
const NOT_A_STREET_NAME = new RegExp(
  [
    '^(?:',
    'P\\.?\\s?O\\.?\\s*BOX|POB',
    '|R\\.?R\\.?\\s*\\d+(?:\\s+BOX)?|H\\.?C\\.?\\s*\\d+(?:\\s+BOX)?',
    '|[NSEW]|NE|NW|SE|SW',
    `|(?:${UNIT_DESIGNATOR_ALTERNATION})\\.?`,
    ')$',
  ].join(''),
  'i',
);

/**
 * Parse street address to extract address number, street name and sub-address
 * e.g., "123 MAIN ST"
 *   -> { addressNumber: "123", streetName: "MAIN ST", subAddress: "" }
 * e.g., "55 COLLINS AVE # 212"
 *   -> { addressNumber: "55", streetName: "COLLINS AVE", subAddress: "# 212" }
 */
export function parseStreetAddress(streetAddress: string): {
  addressNumber: string;
  streetName: string;
  subAddress: string;
} {
  if (!streetAddress) {
    return { addressNumber: '', streetName: '', subAddress: '' };
  }

  const trimmed = streetAddress.trim();

  // Peel the house number first so the designator split only sees the street
  // portion; this keeps addressNumber behaviour identical to before.
  const numberMatch = trimmed.match(/^(\d+[A-Za-z]?)\s+(.+)$/);
  const addressNumber = numberMatch ? numberMatch[1] : '';
  const remainder = numberMatch ? numberMatch[2] : trimmed;

  const unitMatch = remainder.match(SUB_ADDRESS_PATTERN);

  if (unitMatch) {
    // The street name feeds an exact-match master-address lookup, so drop the
    // separator the unit was split off from ("COLLINS AVE," -> "COLLINS AVE").
    const streetName = unitMatch[1]
      .replace(/\s+/g, ' ')
      .replace(/[\s,;.]+$/, '')
      .trim();

    if (streetName && !NOT_A_STREET_NAME.test(streetName)) {
      return {
        addressNumber,
        streetName,
        // Keep the designator verbatim ("# 212", "APT 4B") so it matches what is
        // printed on the license; only collapse whitespace runs.
        subAddress: (unitMatch[2] || unitMatch[3]).replace(/\s+/g, ' ').trim(),
      };
    }
  }

  return { addressNumber, streetName: remainder, subAddress: '' };
}

/**
 * Validate if the scanned data is a valid NY State ID
 */
function isValidNYStateID(rawData: string): boolean {
  if (!rawData.startsWith('@')) return false;

  const hasAAMVA = rawData.includes('ANSI') || rawData.includes('AAMVA');
  if (!hasAAMVA) return false;

  const isNYState = rawData.includes('636001');

  return isNYState;
}

/**
 * Known AAMVA field codes that we need to extract
 */

const KNOWN_FIELD_CODES = [
  'DAA',
  'DAB',
  'DAC',
  'DAD',
  'DAE',
  'DAF',
  'DAG',
  'DAH',
  'DAI',
  'DAJ',
  'DAK',
  'DAL',
  'DAM',
  'DAN',
  'DAO',
  'DAP',
  'DAQ',
  'DAR',
  'DAS',
  'DAT',
  'DAU',
  'DAV',
  'DAW',
  'DAX',
  'DAY',
  'DAZ',
  'DBA',
  'DBB',
  'DBC',
  'DBD',
  'DBE',
  'DBF',
  'DBG',
  'DBH',
  'DBI',
  'DBJ',
  'DBK',
  'DBL',
  'DBM',
  'DBN',
  'DBO',
  'DBP',
  'DBQ',
  'DBR',
  'DBS',
  'DCA',
  'DCB',
  'DCC',
  'DCD',
  'DCE',
  'DCF',
  'DCG',
  'DCH',
  'DCI',
  'DCJ',
  'DCK',
  'DCL',
  'DCM',
  'DCN',
  'DCO',
  'DCP',
  'DCQ',
  'DCR',
  'DCS',
  'DCT',
  'DCU',
  'DCV',
  'DCW',
  'DCX',
  'DCY',
  'DCZ',
  'DDA',
  'DDB',
  'DDC',
  'DDD',
  'DDE',
  'DDF',
  'DDG',
  'DDH',
  'DDI',
  'DDJ',
  'DDK',
  'DDL',
  'DDM',
  'DDN',
  'DDO',
  'DDP',
  'DDQ',
  'DDR',
  'DDS',
  'ZNA',
  'ZNB',
  'ZNC',
  'ZND',
  'ZNE',
  'ZNF',
  'ZNG',
  'ZNH',
  'ZNI',
];

/**
 * Extract field values from the raw barcode data
 * Handles both newline-delimited and fixed-width concatenated formats
 */
function extractFields(rawData: string): Record<string, string> {
  const fields: Record<string, string> = {};

  // First, try parsing newline-delimited format
  const lines = rawData.split(/[\r\n]+/);
  for (const line of lines) {
    if (
      line.startsWith('@') ||
      line.startsWith('ANSI') ||
      line.startsWith('AAMVA')
    ) {
      continue;
    }

    // Check if line starts with a known field code
    for (const code of KNOWN_FIELD_CODES) {
      if (line.startsWith(code)) {
        const value = line.substring(code.length).trim();
        if (value) {
          fields[code] = value;
        }
        break;
      }
    }
  }

  // Second pass: parse concatenated format (no delimiters between fields)
  // This handles barcodes where all data is on one line
  const allText = rawData.replace(/[\r\n]/g, '');

  // Build a regex that matches known field codes and captures everything until the next code
  const codePattern = KNOWN_FIELD_CODES.join('|');
  const concatPattern = new RegExp(
    `(${codePattern})([\\s\\S]*?)(?=${codePattern}|ZN[A-Z]|$)`,
    'g',
  );
  let concatMatch;

  while ((concatMatch = concatPattern.exec(allText)) !== null) {
    const code = concatMatch[1];
    const value = concatMatch[2].trim();

    // Only set if we don't already have this field or current value is longer
    if (value && (!fields[code] || value.length > fields[code].length)) {
      fields[code] = value;
    }
  }

  return fields;
}

/**
 * Main parser function - parses raw scanner input into structured data
 */
export function parseIDCard(rawData: string): IDCardParseResult {
  if (!rawData || rawData.trim().length === 0) {
    return {
      success: false,
      data: null,
      error: 'No data received from scanner',
    };
  }

  if (!isValidNYStateID(rawData)) {
    return {
      success: false,
      data: null,
      error: 'Invalid ID format. Only New York State ID is supported.',
      rawData,
    };
  }

  try {
    const fields = extractFields(rawData);

    const parsedAddress = parseStreetAddress(fields.DAG || '');

    const parsedData: ParsedIDCard = {
      idNumber: fields.DAQ || '',
      customerIdNumber: fields.DBJ || fields.DAQ || '',
      documentNumber: fields.DCF || '',
      state: fields.DAJ || 'NY',
      issueDate: parseDate(fields.DBD || ''),
      expirationDate: parseDate(fields.DBA || ''),
      revisionDate: parseDate(fields.DDB || ''),
      vehicleClass: fields.DCA || '',
      restrictionCodes: fields.DCB || '',
      endorsementCodes: fields.DCD || '',
      documentDiscriminator: fields.DCK || '',
      lastName: fields.DCS || '',
      firstName: fields.DAC || fields.DCT || '',
      middleName: fields.DAD || '',
      nameSuffix: fields.DCU || '',
      dateOfBirth: parseDate(fields.DBB || ''),
      sex: SEX_CODES[fields.DBC] || fields.DBC || '',
      eyeColor: EYE_COLOR_CODES[fields.DAY] || fields.DAY || '',
      height: parseHeight(fields.DAU || ''),
      addressNumber: parsedAddress.addressNumber,
      streetAddress1: parsedAddress.streetName,
      // DAH is the jurisdiction's own street-line-2 field, so it wins; the unit
      // parsed out of DAG is only a fallback. DAG is stripped either way, so
      // completeStreetName never carries a unit designator.
      streetAddress2: (fields.DAH || '').trim() || parsedAddress.subAddress,
      city: fields.DAI || '',
      addressState: fields.DAJ || '',
      zipCode: parseZipCode(fields.DAK || ''),
      // Convert 3-letter country code (USA) to 2-letter ISO code (US)
      country:
        fields.DCG === 'USA' || !fields.DCG ? 'US' : fields.DCG.substring(0, 2),
    };

    return {
      success: true,
      data: parsedData,
      rawData,
    };
  } catch (error) {
    return {
      success: false,
      data: null,
      error: `Failed to parse ID card data: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`,
      rawData,
    };
  }
}

/**
 * Field display names for the comparison table
 */
export const FIELD_DISPLAY_NAMES: Record<keyof ParsedIDCard, string> = {
  idNumber: 'ID Number',
  customerIdNumber: 'Customer ID Number',
  documentNumber: 'Document Number',
  state: 'State',
  issueDate: 'Issue Date',
  expirationDate: 'Expiration Date',
  revisionDate: 'Revision Date',
  vehicleClass: 'Vehicle Class',
  restrictionCodes: 'Restriction Code(s)',
  endorsementCodes: 'Endorsement Code(s)',
  documentDiscriminator: 'Document Discriminator',
  lastName: 'Last Name',
  firstName: 'First Name',
  middleName: 'Middle Name',
  nameSuffix: 'Name Suffix',
  dateOfBirth: 'Date of Birth',
  sex: 'Sex',
  eyeColor: 'Eye Color',
  height: 'Height',
  addressNumber: 'Address Number',
  streetAddress1: 'Street Name',
  streetAddress2: 'Sub-address',
  city: 'City',
  addressState: 'State',
  zipCode: 'Postal Code',
  country: 'Country',
};

/**
 * Maps parsed ID card fields to the person form fields
 */
export const PERSON_FORM_FIELD_MAPPING: Partial<
  Record<keyof ParsedIDCard, string>
> = {
  idNumber: 'driverLicense.number',
  customerIdNumber: 'driverLicense.customerIdNumber',
  documentNumber: 'driverLicense.documentNumber',
  state: 'driverLicense.state',
  issueDate: 'driverLicense.issueDate',
  expirationDate: 'driverLicense.expireDate',
  revisionDate: 'driverLicense.revisionDate',
  vehicleClass: 'driverLicense.vehicleClass',
  restrictionCodes: 'driverLicense.restrictionCodes',
  endorsementCodes: 'driverLicense.endorsementCodes',
  documentDiscriminator: 'driverLicense.documentDiscriminator',
  lastName: 'lastName',
  firstName: 'firstName',
  middleName: 'middleName',
  nameSuffix: 'suffix',
  dateOfBirth: 'birthday',
  sex: 'sex',
  eyeColor: 'physicalCharacteristics.eyeColor',
  addressNumber: 'addresses.0.addressNumber',
  streetAddress1: 'addresses.0.completeStreetName',
  streetAddress2: 'addresses.0.subAddress',
  city: 'addresses.0.placeName',
  addressState: 'addresses.0.state',
  zipCode: 'addresses.0.zipcode',
  country: 'addresses.0.country',
};

export default parseIDCard;
